import React from "react";
import {
	styled,
	Collapse,
	Grid2 as Grid,
	IconButton,
	TableCell,
	TableRow,
	Typography,
	TextField,
	Badge,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CurrencyTextField from "../CurrencyTextField/CurrencyTextField";
import { isLineItemCancelled, isLineItemOverReceived } from "../../util/lineItemUtil";
import { Alert } from "@mui/material";
import { BlockOutlined } from "@mui/icons-material";
import { cancelledLineItemMessage, overReceivedLineItemMessage } from "../../util/messagingUtil";
import EditLocationOutlined from "@mui/icons-material/EditLocationOutlined";
import { PriorityStockFlag } from "../PriorityStock/PriorityStockFlag";
import { useStyles } from "./Invoice.styles";
import { getAvailabileForCustomerOrderQty } from "../../util/customerOrderUtil";
import { ManualReceiveLineItemRowProps } from "./types";

const StyledTableCell = styled(TableCell)({
	padding: 0,
});

export const EditableManualReceiveLineItemRow: React.FC<ManualReceiveLineItemRowProps<true>> = (props) => {
	return <ManualReceiveLineItemRow {...props} isEditable={true} />;
};

export const ReadonlyManualReceiveLineItemRow: React.FC<ManualReceiveLineItemRowProps> = (props) => {
	return <ManualReceiveLineItemRow {...props} isEditable={false} />;
};

const ManualReceiveLineItemRow: React.FC<ManualReceiveLineItemRowProps & { isEditable: boolean }> = (props) => {
	const {
		lineItem,
		isHardwareView,
		isLocationBayEditable,
		showDetailsOnly,
		showOverReceive,
		onLocationNameChange,
		taxRate,
		priorityFlag,
		priorityReason,
		isArrivalsFeatureEnabled,
		isPriorityStockEnabled,
		isEditable,
	} = props;
	const [open, setOpen] = React.useState(false);
	const classes = useStyles();

	function isRowEditable(
		props: ManualReceiveLineItemRowProps<boolean> & { isEditable: boolean }
	): props is ManualReceiveLineItemRowProps<true> & { isEditable: boolean } {
		return props.isEditable;
	}

	//Function details: onCostChange(invoiceNumber, Sku, unitInc, unitEx, lineInc, lineEx)
	const onLineIncChange = (value) => {
		if (isRowEditable(props)) {
			props.onCostChange(
				lineItem.sku,
				value / lineItem.receivedQuantity,
				value / (1 + taxRate) / lineItem.receivedQuantity,
				value,
				value / (1 + taxRate)
			);
		}
	};
	const onLineExChange = (value) => {
		if (isRowEditable(props)) {
			props.onCostChange(
				lineItem.sku,
				(value * (1 + taxRate)) / lineItem.receivedQuantity,
				value / lineItem.receivedQuantity,
				value * (1 + taxRate),
				value
			);
		}
	};
	const onUnitIncChange = (value) => {
		if (isRowEditable(props)) {
			props.onCostChange(
				lineItem.sku,
				value,
				value / (1 + taxRate),
				value * lineItem.receivedQuantity,
				(value / (1 + taxRate)) * lineItem.receivedQuantity
			);
		}
	};
	const onUnitExChange = (value) => {
		if (isRowEditable(props)) {
			props.onCostChange(
				lineItem.sku,
				value * (1 + taxRate),
				value,
				value * (1 + taxRate) * lineItem.receivedQuantity,
				value * lineItem.receivedQuantity
			);
		}
	};

	const onReceivedQtyChange = (e) => {
		if (isRowEditable(props)) {
			let inputValue = e.target.value.replace(/^0+/, "");

			if (!inputValue || inputValue < 0) {
				inputValue = 0;
			}
			props.onReceivedQuantityChange(lineItem.sku, parseInt(inputValue, 10));
		}
	};

	const getPercentComplete = () => {
		return (lineItem.receivedQuantity / lineItem.orderedQuantity) * 100;
	};

	const getTheme = () => {
		// Part Cancelled
		if (isCancelled) {
			return { background: classes.warn, border: classes.warnBorder };
		}
		if (isOverReceived) {
			return { background: classes.over, border: classes.overBorder };
		}

		// Progressive
		const percent = getPercentComplete();
		if (percent === 100) {
			return { background: classes.full, border: classes.fullBorder };
		} else if (percent > 0) {
			return { background: classes.partial, border: classes.partialBorder };
		} else {
			return { background: classes.none, border: classes.noneBorder };
		}
	};

	const isCancelled = isLineItemCancelled(lineItem);
	const isOverReceived = showOverReceive && isLineItemOverReceived(lineItem) && !isCancelled;
	const customerOrderAllocatedQty = lineItem.customerOrderAllocatedQty
		? lineItem.customerOrderAllocatedQty
		: getAvailabileForCustomerOrderQty(
				lineItem.orderedQuantity,
				lineItem.originalReceivedQuantity,
				lineItem.inventory?.quantity
			);
	return (
		<React.Fragment>
			<TableRow className={showDetailsOnly ? getTheme().background : undefined}>
				<StyledTableCell className={getTheme().border} style={{ width: "40px" }}>
					<IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
						{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
					</IconButton>
				</StyledTableCell>
				<StyledTableCell>
					<Badge
						overlap="rectangular"
						classes={{ badge: classes.badge }}
						className={classes.badgePadding}
						badgeContent={customerOrderAllocatedQty || 0}
					>
						<Typography variant="body2">{lineItem.sku}</Typography>
					</Badge>
				</StyledTableCell>
				<StyledTableCell align="center">
					<Typography variant="body2" className={classes.caption}>
						{lineItem.orderedQuantity}
					</Typography>
				</StyledTableCell>
				<StyledTableCell align="center">
					{isEditable ? (
						<TextField
							id="invQty"
							value={lineItem.receivedQuantity ? lineItem.receivedQuantity.toString() : "0"}
							onChange={onReceivedQtyChange}
							type="number"
							variant="standard"
							size="small"
							slotProps={{ htmlInput: { style: { textAlign: "center" }, min: 0 } }}
							autoComplete="off"
						/>
					) : (
						<Typography variant="body2" className={classes.caption}>
							{lineItem.receivedQuantity}
						</Typography>
					)}
				</StyledTableCell>
				{showDetailsOnly && (
					<StyledTableCell align="center">
						<Typography variant="body2" className={classes.caption}>
							{lineItem.orderedQuantity - lineItem.receivedQuantity}
						</Typography>
					</StyledTableCell>
				)}
			</TableRow>
			<TableRow>
				<StyledTableCell
					className={getTheme().border}
					style={{
						textAlign: "center",
						verticalAlign: "middle",
						width: "40px",
					}}
				>
					{isLocationBayEditable && (
						<IconButton
							aria-label="Change location bay"
							size="small"
							onClick={() => onLocationNameChange && onLocationNameChange(lineItem.inboundLocation, lineItem.sku)}
						>
							<EditLocationOutlined />
						</IconButton>
					)}
				</StyledTableCell>
				<StyledTableCell colSpan={showDetailsOnly ? 4 : 3}>
					<Grid container>
						<Grid container size={12}>
							<Grid size={3}>
								<Typography variant="body2" color="textSecondary">
									Model
								</Typography>
							</Grid>
							<Grid size={9}>
								<Typography variant="body2" noWrap>
									{lineItem.model}
								</Typography>
							</Grid>
						</Grid>
						{lineItem.barcode && (
							<Grid container size={12}>
								<Grid size={3}>
									<Typography variant="body2" color="textSecondary">
										Barcode
									</Typography>
								</Grid>
								<Grid size={9}>
									<Typography variant="body2" noWrap>
										{lineItem.barcode}
									</Typography>
								</Grid>
							</Grid>
						)}
						<Grid container size={12}>
							<Grid size={3}>
								<Typography variant="body2" color="textSecondary">
									Desc
								</Typography>
							</Grid>
							<Grid size={9}>
								<Typography variant="body2" noWrap>
									{lineItem.description}
								</Typography>
							</Grid>
						</Grid>
						{!isHardwareView && (
							<Grid container size={12}>
								<Grid size={3}>
									<Typography variant="body2" color="textSecondary">
										Artist
									</Typography>
								</Grid>
								<Grid size={9}>
									<Typography variant="body2" noWrap>
										{lineItem.artist}
									</Typography>
								</Grid>
							</Grid>
						)}
						{!isHardwareView && lineItem.productGroup && (
							<Grid container size={12}>
								<Grid size={3}>
									<Typography variant="body2" color="textSecondary">
										Group
									</Typography>
								</Grid>
								<Grid size={9}>
									<Typography variant="body2" noWrap>
										{lineItem.productGroup}
									</Typography>
								</Grid>
							</Grid>
						)}
						{!isHardwareView && (
							<Grid container size={12}>
								<Grid size={3}>
									<Typography variant="body2" color="textSecondary">
										Format
									</Typography>
								</Grid>
								<Grid size={9}>
									<Typography variant="body2" noWrap>
										{lineItem.format}
									</Typography>
								</Grid>
							</Grid>
						)}
						<Grid container size={12}>
							{lineItem.ticketPrice && (
								<Grid container size={6}>
									<Grid size={6}>
										<Typography variant="body2" color="textSecondary">
											Ticket
										</Typography>
									</Grid>
									<Grid size={6}>
										<Typography variant="body2">${lineItem.ticketPrice?.toFixed(2)}</Typography>
									</Grid>
								</Grid>
							)}
							{lineItem.promoPrice > 0 && (
								<Grid container size={6}>
									<Grid size={6}>
										<Typography variant="caption" color="textSecondary">
											Promo
										</Typography>
									</Grid>
									<Grid size={6}>
										<Typography variant="body2" className={classes.highlight}>
											${lineItem.promoPrice?.toFixed(2)}
										</Typography>
									</Grid>
								</Grid>
							)}
						</Grid>
						{priorityFlag && (isArrivalsFeatureEnabled || isPriorityStockEnabled) && (
							<Grid container size={12}>
								<Grid size={12}>
									<PriorityStockFlag flag={priorityFlag} />
								</Grid>
							</Grid>
						)}
						{priorityReason && (isArrivalsFeatureEnabled || isPriorityStockEnabled) && (
							<Grid container size={12}>
								<Grid size={12}>
									<Typography variant="caption" color="textSecondary">
										{priorityReason}
									</Typography>
								</Grid>
							</Grid>
						)}
						{isCancelled && (
							<Grid container size={12}>
								<Alert icon={<BlockOutlined fontSize="inherit" />} severity="error">
									{cancelledLineItemMessage(lineItem.sku)}
								</Alert>
							</Grid>
						)}
						{isOverReceived && (
							<Grid container size={12}>
								<Alert severity="warning">{overReceivedLineItemMessage(lineItem.sku)}</Alert>
							</Grid>
						)}
					</Grid>
				</StyledTableCell>
			</TableRow>
			<TableRow>
				<StyledTableCell style={{ width: "40px" }}></StyledTableCell>
				<StyledTableCell colSpan={showDetailsOnly ? 4 : 3}>
					<Collapse in={open} timeout="auto" unmountOnExit>
						<Grid container>
							<Grid container size={12}>
								<Grid size={4}>
									<Typography variant="body2" color="textSecondary">
										Cost Type
									</Typography>
								</Grid>
								<Grid size={4}>
									<Typography variant="body2" color="textSecondary">
										PO
									</Typography>
								</Grid>
								<Grid size={4}>
									{!showDetailsOnly ? (
										<Typography variant="body2" color="textSecondary">
											Invoice
										</Typography>
									) : null}
								</Grid>
							</Grid>
							<Grid container size={12} alignItems="center">
								<Grid size={4}>
									<Typography variant="body2" color="textSecondary">
										Unit Cost Ex
									</Typography>
								</Grid>
								<Grid size={4}>
									<Typography variant="body2">${lineItem.purchaseOrderUnitCostEx?.toFixed(2)}</Typography>
								</Grid>
								<Grid size={4}>
									{!showDetailsOnly ? (
										isEditable ? (
											<CurrencyTextField
												id="unitEx"
												value={lineItem.unitCostEx}
												onChange={(event, value) => onUnitExChange(value)}
												currencySymbol="$"
												minimumValue="0"
											/>
										) : (
											<Typography variant="body2">${lineItem.unitCostEx?.toFixed(2)}</Typography>
										)
									) : null}
								</Grid>
							</Grid>
							<Grid container size={12} alignItems="center">
								<Grid size={4}>
									<Typography variant="body2" color="textSecondary">
										Unit Cost Inc
									</Typography>
								</Grid>
								<Grid size={4}>
									<Typography variant="body2">${lineItem.purchaseOrderUnitCostInc?.toFixed(2)}</Typography>
								</Grid>
								<Grid size={4}>
									{!showDetailsOnly ? (
										isEditable ? (
											<CurrencyTextField
												id="unitInc"
												value={lineItem.unitCostInc}
												onChange={(event, value) => onUnitIncChange(value)}
												currencySymbol="$"
												minimumValue="0"
											/>
										) : (
											<Typography variant="body2">${lineItem.unitCostInc?.toFixed(2)}</Typography>
										)
									) : null}
								</Grid>
							</Grid>
							<Grid container size={12} alignItems="center">
								<Grid size={4}>
									<Typography variant="body2" color="textSecondary">
										Line Total Ex
									</Typography>
								</Grid>
								<Grid size={4}>
									<Typography variant="body2">${lineItem.purchaseOrderLineTotalCostEx?.toFixed(2)}</Typography>
								</Grid>
								<Grid size={4}>
									{!showDetailsOnly ? (
										isEditable ? (
											<CurrencyTextField
												id="lineEx"
												value={lineItem.lineTotalCostEx}
												onChange={(event, value) => onLineExChange(value)}
												currencySymbol="$"
												minimumValue="0"
											/>
										) : (
											<Typography variant="body2">${lineItem.lineTotalCostEx?.toFixed(2)}</Typography>
										)
									) : null}
								</Grid>
							</Grid>
							<Grid container size={12} alignItems="center">
								<Grid size={4}>
									<Typography variant="body2" color="textSecondary">
										Line Total Inc
									</Typography>
								</Grid>
								<Grid size={4}>
									<Typography variant="body2">${lineItem.purchaseOrderLineTotalCostInc?.toFixed(2)}</Typography>
								</Grid>
								<Grid size={4}>
									{!showDetailsOnly ? (
										isEditable ? (
											<CurrencyTextField
												id="lineEx"
												value={lineItem.lineTotalCostInc}
												onChange={(event, value) => onLineIncChange(value)}
												currencySymbol="$"
												minimumValue="0"
											/>
										) : (
											<Typography variant="body2">${lineItem.lineTotalCostInc?.toFixed(2)}</Typography>
										)
									) : null}
								</Grid>
							</Grid>
						</Grid>
					</Collapse>
				</StyledTableCell>
			</TableRow>
		</React.Fragment>
	);
};
